import { Modal, ModalBody, Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IconTrash2 from "@/icons/IconTrash2";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import IconCheckMark from "@/icons/IconCheckMark";
import { IconClose } from "@velaro/velaro-component-lib";
import { AlertTriangle } from "@velaro/velaro-component-lib";
import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PrimaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { useNavigate } from "react-router-dom";
import useToastAlert from "@/hooks/useToastAlert";
import {
  Edit as IconPen,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown
} from "@/components/ds/icons";
import { PrimaryButton, SecondaryButton, SectionCard } from "@/components/ds";

type HealthStatus =
  "Healthy" | "Degraded" | "Failed" | "NotConfigured" | string;

interface IntegrationHealthResult {
  slug: string;
  siteId: number;
  status: HealthStatus;
  latencyMs?: number | null;
  marker?: string | null;
  detail?: string | null;
  checkedAt?: string | null;
  consecutiveFailures?: number;
  circuitOpenUntil?: string | null;
  firstHealthyAt?: string | null;
}

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  consumerKey: string;
  consumerSecret: string;
  token: string;
  tokenSecret: string;
  restletUrl: string;
  teamId: number;
  authType?: string | null;
  pushTranscripts?: boolean;
  transcriptPushMode?: string;
  transcriptFieldName?: string;
  transcriptRestletUrl?: string;
  defaultSubsidiaryId?: string;
  defaultLeadEntityStatusId?: string;
  newContactTargetRecordType?: string;
  matchStrategy?: string;
  noMatchAction?: string;
  maxTranscriptFieldLength?: number | string | null;
}

interface Props {
  configs: NetSuiteConfig[];
  deleteConfig(id: number): void;
  addConfig(): void;
  teamId: number;
  onChange(
    id: number,
    field: keyof NetSuiteConfig,
    value: string | boolean
  ): void;
  onSelectConfig(id: number): void;
  onCreateMode?: boolean;
}

export default function NetSuiteAccounts(props: Props) {
  const [showSetupModal, setShowSetupModal] = useState(false);
  const api = useApi();
  const [company, setCompany] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [token, setToken] = useState("");
  const [tokenSecret, setTokenSecret] = useState("");
  const [restletUrl, setRestletUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();
  const [selectedConfigId, setSelectedConfigId] = useState(
    props.configs.length === 1 ? props.configs[0].id : 0
  );
  const [selectedConfig, setSelectedConfig] = useState<NetSuiteConfig | null>(
    props.configs.length > 0 ? props.configs[0] : null
  );

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    displayName: string;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);
  const consumerKeyInputRef = useRef<HTMLInputElement>(null);
  const consumerSecretInputRef = useRef<HTMLInputElement>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const tokenSecretInputRef = useRef<HTMLInputElement>(null);
  const restletUrlInputRef = useRef<HTMLInputElement>(null);
  const [showConsumerKey, setShowConsumerKey] = useState(false);
  const [showConsumerSecret, setShowConsumerSecret] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showTokenSecret, setShowTokenSecret] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [consumerKeyFocused, setConsumerKeyFocused] = useState(false);
  const [consumerSecretFocused, setConsumerSecretFocused] = useState(false);

  // Named RESTlets state
  const [restlets, setRestlets] = useState<
    {
      id: number;
      name: string;
      displayName: string;
      url: string;
      isEnabled: boolean;
    }[]
  >([]);
  const [addingRestlet, setAddingRestlet] = useState(false);
  const [newRestlet, setNewRestlet] = useState({
    name: "",
    displayName: "",
    url: ""
  });
  const [savingRestlet, setSavingRestlet] = useState(false);
  const [tokenFocused, setTokenFocused] = useState(false);
  const [tokenSecretFocused, setTokenSecretFocused] = useState(false);
  const [displayNameFocused, setDisplayNameFocused] = useState(false);
  const [restletUrlFocused, setRestletUrlFocused] = useState(false);
  const toast = useToastAlert();
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    consumerKey: "",
    consumerSecret: "",
    token: "",
    tokenSecret: "",
    restletUrl: ""
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (props.configs.length > 0) {
      if (selectedConfigId) {
        const existingConfig = props.configs.find(
          (c) => c.id === selectedConfigId
        );
        if (existingConfig) {
          setSelectedConfig(existingConfig);
          return;
        }
      }

      const lastConfig = props.configs[props.configs.length - 1];
      setSelectedConfigId(lastConfig.id);
      setSelectedConfig(lastConfig);
      props.onSelectConfig(lastConfig.id);
    } else {
      setSelectedConfig(null);
    }
  }, [props.configs]);

  useEffect(() => {
    if (!selectedConfig) {
      setRestlets([]);
      return;
    }
    api.messaging
      .get(`NetSuite/Restlets/${selectedConfig.id}`)
      .then((r) => r.json())
      .then(setRestlets)
      .catch(() => setRestlets([]));
  }, [selectedConfig?.id]);

  // Real connection health — backed by the IntegrationHealthScheduler probe that already runs
  // every 15 min in velaro-messaging (NetSuiteHealthCheck) and persists to IntegrationHealthResults.
  // Note: the health check is site-scoped, not per-config (it probes the site's first/primary
  // NetSuite config by Id) — so the same result is shown regardless of which config is selected
  // if a site has more than one NetSuite connection. That's a backend granularity gap, not a bug
  // here; flagged for the messaging-side follow-up rather than guessed at on the frontend.
  const [health, setHealth] = useState<IntegrationHealthResult | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => {
    if (!selectedConfig) {
      setHealth(null);
      return;
    }
    let cancelled = false;
    setHealthLoading(true);
    api.messaging
      .get(`Integration/Health/NetSuite`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) {
          setHealth(null);
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setHealth(await r.json());
      })
      .catch(() => {
        if (!cancelled) setHealth(null);
      })
      .finally(() => {
        if (!cancelled) setHealthLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedConfig?.id]);

  const handleAddRestlet = async () => {
    if (
      !selectedConfig ||
      !newRestlet.name ||
      !newRestlet.displayName ||
      !newRestlet.url
    )
      return;
    setSavingRestlet(true);
    try {
      const resp = await api.messaging.post(
        `NetSuite/Restlets/${selectedConfig.id}`,
        newRestlet
      );
      if (resp.ok) {
        const data = await api.messaging.get(
          `NetSuite/Restlets/${selectedConfig.id}`
        );
        setRestlets(await data.json());
        setNewRestlet({ name: "", displayName: "", url: "" });
        setAddingRestlet(false);
        toast.displayToast(Severity.Success, "RESTlet registered.");
      } else {
        const msg = await resp.text();
        toast.displayToast(Severity.Error, msg || "Failed to add RESTlet.");
      }
    } catch {
      toast.displayToast(Severity.Error, "Failed to add RESTlet.");
    }
    setSavingRestlet(false);
  };

  const handleDeleteRestlet = async (id: number, name: string) => {
    if (!selectedConfig) return;
    if (!window.confirm(`Remove RESTlet '${name}'?`)) return;
    try {
      await api.messaging.delete(
        `NetSuite/Restlets/${selectedConfig.id}/${id}`
      );
      setRestlets((prev) => prev.filter((r) => r.id !== id));
      toast.displayToast(Severity.Success, "RESTlet removed.");
    } catch {
      toast.displayToast(Severity.Error, "Failed to remove RESTlet.");
    }
  };

  const handleTestRestlet = async (id: number, name: string) => {
    if (!selectedConfig) return;
    try {
      const resp = await api.messaging.post(
        `NetSuite/Restlets/${selectedConfig.id}/${id}/test`,
        {}
      );
      const data = await resp.json();
      if (data.success) {
        toast.displayToast(
          Severity.Success,
          `'${name}' responded: ${JSON.stringify(data.response ?? "ok")}`
        );
      } else {
        toast.displayToast(
          Severity.Error,
          `'${name}' test failed: ${data.error}`
        );
      }
    } catch {
      toast.displayToast(Severity.Error, "Test call failed. Check network.");
    }
  };

  const handleSelect = (config: NetSuiteConfig) => {
    if (editingId !== config.id) {
      setSelectedConfig(config);
      setSelectedConfigId(config.id);
      props.onSelectConfig(config.id);
      setIsOpen(false);
    }
  };

  const handleDeleteClick = (id: number, displayName: string) => {
    setConfigToDelete({ id, displayName });
    setIsModalOpen(true);
  };

  const confirmDelete = async (id?: number) => {
    if (!id) return;
    await api.messaging.delete(`NetSuite/Integrations/${id}`);
    props.deleteConfig(id);
    setIsModalOpen(false);
    setConfigToDelete(null);
  };

  const [togglingTranscripts, setTogglingTranscripts] = useState(false);
  const [savingContactTarget, setSavingContactTarget] = useState(false);
  const [leadStatusDraft, setLeadStatusDraft] = useState<string | null>(null);
  const [savingMatchSettings, setSavingMatchSettings] = useState(false);
  const [maxLengthDraft, setMaxLengthDraft] = useState<string | null>(null);

  // Match Strategy / No-Match Action / transcript field cap / transcript history mode — same
  // nullable partial-update PUT as Contact Creation above. Defaults (unset) are byte-for-byte
  // today's behavior: emailThenPhone matching, skip on no match, full 100,000-char server cap,
  // "field" push mode. transcriptPushMode/transcriptRestletUrl are routed through this same
  // helper (not a parallel one) because PUT NetSuite/Integrations/{id} applies every field
  // only-if-present and echoes all of them back in one response.
  const saveMatchSettings = async (
    config: NetSuiteConfig,
    fields: {
      matchStrategy?: string;
      noMatchAction?: string;
      maxTranscriptFieldLength?: number;
      transcriptPushMode?: string;
      transcriptRestletUrl?: string;
    }
  ) => {
    setSavingMatchSettings(true);
    try {
      const response = await api.messaging.put(
        `NetSuite/Integrations/${config.id}`,
        fields
      );
      if (response.ok) {
        const updated = await response.json();
        if (fields.matchStrategy !== undefined) {
          props.onChange(
            config.id,
            "matchStrategy",
            updated.matchStrategy ?? ""
          );
        }
        if (fields.noMatchAction !== undefined) {
          props.onChange(
            config.id,
            "noMatchAction",
            updated.noMatchAction ?? ""
          );
        }
        if (fields.maxTranscriptFieldLength !== undefined) {
          props.onChange(
            config.id,
            "maxTranscriptFieldLength",
            String(updated.maxTranscriptFieldLength ?? "")
          );
        }
        if (fields.transcriptPushMode !== undefined) {
          props.onChange(config.id, "transcriptPushMode", updated.transcriptPushMode ?? "field");
        }
        if (fields.transcriptRestletUrl !== undefined) {
          props.onChange(config.id, "transcriptRestletUrl", updated.transcriptRestletUrl ?? "");
        }
      } else {
        const msg = await response.text();
        toast.displayToast(
          Severity.Error,
          msg || "Failed to update transcript match settings."
        );
      }
    } catch {
      toast.displayToast(
        Severity.Error,
        "Failed to update transcript match settings."
      );
    }
    setSavingMatchSettings(false);
  };

  // Contact Creation Target — cross-integration setting ("Lead" default | "Customer").
  // Saves immediately on change via the same nullable partial-update PUT as the
  // transcript toggle; unrelated fields are never sent, never reset.
  const saveContactCreation = async (
    config: NetSuiteConfig,
    fields: {
      newContactTargetRecordType?: string;
      defaultLeadEntityStatusId?: string;
    }
  ) => {
    setSavingContactTarget(true);
    try {
      const response = await api.messaging.put(
        `NetSuite/Integrations/${config.id}`,
        fields
      );
      if (response.ok) {
        const updated = await response.json();
        if (fields.newContactTargetRecordType !== undefined) {
          props.onChange(
            config.id,
            "newContactTargetRecordType",
            updated.newContactTargetRecordType ?? ""
          );
        }
        if (fields.defaultLeadEntityStatusId !== undefined) {
          props.onChange(
            config.id,
            "defaultLeadEntityStatusId",
            updated.defaultLeadEntityStatusId ?? ""
          );
        }
      } else {
        const msg = await response.text();
        toast.displayToast(
          Severity.Error,
          msg || "Failed to update contact creation setting."
        );
      }
    } catch {
      toast.displayToast(
        Severity.Error,
        "Failed to update contact creation setting."
      );
    }
    setSavingContactTarget(false);
  };

  const toggleTranscriptPush = async (config: NetSuiteConfig) => {
    setTogglingTranscripts(true);
    try {
      const response = await api.messaging.put(
        `NetSuite/Integrations/${config.id}`,
        {
          pushTranscripts: !config.pushTranscripts
        }
      );
      if (response.ok) {
        const updated = await response.json();
        props.onChange(config.id, "pushTranscripts", updated.pushTranscripts);
      } else {
        toast.displayToast(
          Severity.Error,
          "Failed to update transcript push setting."
        );
      }
    } catch {
      toast.displayToast(
        Severity.Error,
        "Failed to update transcript push setting."
      );
    }
    setTogglingTranscripts(false);
  };

  const maskValue = (value: string) => {
    if (!value) return "";
    return "•".repeat(value.length);
  };

  const partiallyMaskValue = (value: string) => {
    if (!value) return "";
    if (value.length <= 8) return "•".repeat(value.length);
    const firstChars = value.slice(0, 4);
    const lastChars = value.slice(-4);
    return `${firstChars}${"•".repeat(Math.max(0, value.length - 8))}${lastChars}`;
  };

  const handleEditClick = () => {
    if (selectedConfig) {
      setEditFormData({
        displayName: selectedConfig.displayName,
        consumerKey: "", // User must re-enter
        consumerSecret: "", // User must re-enter
        token: "", // User must re-enter
        tokenSecret: "", // User must re-enter
        restletUrl: selectedConfig.restletUrl
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedConfig) return;

    // Token/TokenSecret are OAuth 1.0a (Token-Based Auth) fields — an OAuth 2.0 connection
    // never has them, so requiring them here blocked EVERY edit on an OAuth 2.0 connection,
    // including just renaming it or re-pointing the RESTlet URL. Found on the V20 production
    // connect 2026-08-05. Unknown/legacy authType keeps the original strict gate.
    // Sending them empty is safe: UpdateCredentialsAsync guards each secret with
    // !string.IsNullOrEmpty && !StartsWith("****"), so empty values are skipped, not written.
    const isOAuth2 = selectedConfig.authType === "oauth2";
    if (!isOAuth2 && (!editFormData.token || !editFormData.tokenSecret)) {
      toast.displayToast(
        Severity.Error,
        "Please enter both token and token secret to update credentials"
      );
      return;
    }

    setSending(true);
    try {
      const response = await api.messaging.post(
        `NetSuite/Auth/UpdateCredentials/${selectedConfig.id}`,
        {
          displayName: editFormData.displayName,
          company: selectedConfig.company,
          consumerKey: editFormData.consumerKey || undefined,
          consumerSecret: editFormData.consumerSecret || undefined,
          token: editFormData.token,
          tokenSecret: editFormData.tokenSecret,
          restletUrl: editFormData.restletUrl
        }
      );

      if (response.ok) {
        props.onChange(
          selectedConfig.id,
          "displayName",
          editFormData.displayName
        );
        // Only mirror the secrets locally when one was actually sent. The server SKIPS empty
        // secrets rather than clearing them, so echoing "" into local state would show a blank
        // token for a connection whose stored token is untouched.
        if (editFormData.token) {
          props.onChange(selectedConfig.id, "token", editFormData.token);
        }
        if (editFormData.tokenSecret) {
          props.onChange(
            selectedConfig.id,
            "tokenSecret",
            editFormData.tokenSecret
          );
        }
        props.onChange(
          selectedConfig.id,
          "restletUrl",
          editFormData.restletUrl
        );
        setIsEditing(false);

        const timer = setTimeout(() => {
          toast.displayToast(
            Severity.Success,
            "Account updated successfully! Audit log entry created."
          );
          clearTimeout(timer);
        }, 3000);
      } else {
        toast.displayToast(
          Severity.Error,
          "Failed to update credentials. Please verify the tokens are correct."
        );
      }
    } catch (error) {
      console.error("Failed to update credentials", error);
      toast.displayToast(
        Severity.Error,
        "Failed to update credentials. Please try again."
      );
    }
    setSending(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({
      displayName: "",
      consumerKey: "",
      consumerSecret: "",
      token: "",
      tokenSecret: "",
      restletUrl: ""
    });
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isEditing) return;

    const input = e.target;

    if (input === displayNameRef.current) {
      setDisplayNameFocused(true);
    } else if (input === consumerKeyInputRef.current) {
      setConsumerKeyFocused(true);
    } else if (input === consumerSecretInputRef.current) {
      setConsumerSecretFocused(true);
    } else if (input === tokenInputRef.current) {
      setTokenFocused(true);
    } else if (input === tokenSecretInputRef.current) {
      setTokenSecretFocused(true);
    } else if (input === restletUrlInputRef.current) {
      setRestletUrlFocused(true);
    }

    setTimeout(() => {
      let length = 0;

      if (input === displayNameRef.current) {
        length = editFormData.displayName.length;
      } else if (input === consumerKeyInputRef.current) {
        length = editFormData.consumerKey.length;
      } else if (input === consumerSecretInputRef.current) {
        length = editFormData.consumerSecret.length;
      } else if (input === tokenInputRef.current) {
        length = editFormData.token.length;
      } else if (input === tokenSecretInputRef.current) {
        length = editFormData.tokenSecret.length;
      } else if (input === restletUrlInputRef.current) {
        length = editFormData.restletUrl.length;
      }

      input.setSelectionRange(length, length);
    }, 0);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;

    if (input === displayNameRef.current) {
      setDisplayNameFocused(false);
    } else if (input === consumerKeyInputRef.current) {
      setConsumerKeyFocused(false);
    } else if (input === consumerSecretInputRef.current) {
      setConsumerSecretFocused(false);
    } else if (input === tokenInputRef.current) {
      setTokenFocused(false);
    } else if (input === tokenSecretInputRef.current) {
      setTokenSecretFocused(false);
    } else if (input === restletUrlInputRef.current) {
      setRestletUrlFocused(false);
    }
  };

  const handleToggleTokenVisibility = () => {
    setShowToken(!showToken);
    if (!showToken && tokenInputRef.current) {
      setTimeout(() => {
        tokenInputRef.current?.focus();
        const length = isEditing
          ? editFormData.token.length
          : (selectedConfig?.token || "").length;
        tokenInputRef.current?.setSelectionRange(length, length);
      }, 0);
    }
  };

  const handleToggleTokenSecretVisibility = () => {
    setShowTokenSecret(!showTokenSecret);
    if (!showTokenSecret && tokenSecretInputRef.current) {
      setTimeout(() => {
        tokenSecretInputRef.current?.focus();
        const length = isEditing
          ? editFormData.tokenSecret.length
          : (selectedConfig?.tokenSecret || "").length;
        tokenSecretInputRef.current?.setSelectionRange(length, length);
      }, 0);
    }
  };

  async function handleSubmit() {
    setSending(true);
    const response = await api.messaging.post("NetSuite/Auth/SaveCredentials", {
      tenantId: tenantId,
      displayName: displayName,
      company: company,
      token: token,
      tokenSecret: tokenSecret,
      restletUrl: restletUrl,
      teamId: props.teamId
    });
    setSuccess(response.ok);
    if (response.ok) {
      props.addConfig();
      setCompany("");
      setDisplayName("");
      setToken("");
      setTokenSecret("");
      setRestletUrl("");
      setShowSetupModal(false);
    }
    setSending(false);
  }

  // ─── Credential field helper ───────────────────────────────────────────────
  function CredentialField({
    label,
    hint,
    inputRef,
    viewValue,
    editValue,
    editPlaceholder,
    showReveal,
    revealed,
    onReveal,
    onFocus,
    onBlur,
    onChange,
    isFocused,
    disabled = false
  }: {
    label: string;
    hint?: string;
    inputRef?: React.RefObject<HTMLInputElement>;
    viewValue: string;
    editValue: string;
    editPlaceholder?: string;
    showReveal?: boolean;
    revealed?: boolean;
    onReveal?: () => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (v: string) => void;
    isFocused?: boolean;
    disabled?: boolean;
  }) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-zinc-600">{label}</label>
        {hint && <p className="text-[10px] text-zinc-400">{hint}</p>}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={isEditing ? editValue : viewValue}
            onChange={(e) => onChange?.(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={isEditing ? (editPlaceholder ?? "") : ""}
            disabled={!isEditing || disabled}
            className={`w-full px-3 py-2 pr-10 border rounded-lg text-zinc-800 text-sm font-normal focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50 ${isFocused && isEditing ? "border-blue-500 ring-1 ring-blue-500" : ""}`}
          />
          {isEditing && !disabled ? (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
              <IconPen className="w-3 h-3" />
            </span>
          ) : showReveal && !isEditing ? (
            <button
              type="button"
              onClick={onReveal}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              {revealed ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // Real connection-health badge. Never relies on color alone — status text is always present.
  function HealthBadge() {
    if (healthLoading) {
      return (
        <span className="flex items-center gap-1 text-[10px] text-zinc-600 bg-zinc-50 border border-zinc-200 px-2 py-0.5 rounded">
          Checking connection…
        </span>
      );
    }

    const status = health?.status;
    // Null firstHealthyAt means this integration has NEVER once reported Healthy for this
    // site — per IntegrationHealthController.cs's MapRow doc comment, a Failed/Degraded status
    // alongside a null firstHealthyAt is a setup problem (bad/missing credentials), not a
    // regression, and must render neutral rather than an alarming red "Needs reconnect" telling
    // a customer to reconnect something they've never successfully connected in the first place.
    const neverHealthy = !health || health.firstHealthyAt == null;

    if (!status || status === "NotConfigured" || neverHealthy) {
      return (
        <span className="flex items-center gap-1 text-[10px] text-zinc-600 bg-zinc-50 border border-zinc-200 px-2 py-0.5 rounded">
          Not connected yet
        </span>
      );
    }

    if (status === "Healthy") {
      return (
        <span className="flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
          <svg
            viewBox="0 0 8 8"
            width="7"
            height="7"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M1 4L3 6L7 2"
              stroke="#16a34a"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Connected
        </span>
      );
    }

    if (status === "Degraded") {
      return (
        <span
          className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded"
          title={health?.detail ?? undefined}
        >
          Degraded
        </span>
      );
    }

    // Failed (or any other unrecognized-but-non-null status — fail toward the loud state)
    return (
      <span
        className="flex items-center gap-1 text-[10px] text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded"
        title={health?.detail ?? undefined}
      >
        Needs reconnect
      </span>
    );
  }

  const headerActions = (
    <div className="flex items-center gap-2">
      {!isEditing && (
        <>
          <button
            type="button"
            onClick={handleEditClick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-600 hover:bg-zinc-50 transition"
            title="Edit credentials"
          >
            <IconEditPencil2 className="w-4" /> Edit
          </button>
          <button
            type="button"
            onClick={() =>
              selectedConfig &&
              handleDeleteClick(selectedConfig.id, selectedConfig.displayName)
            }
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50 transition"
            title="Remove account"
          >
            <IconTrash2 className="w-4" /> Remove
          </button>
        </>
      )}
      {isEditing && (
        <>
          <PrimaryButton onClick={handleSaveEdit} disabled={sending}>
            Save
          </PrimaryButton>
          <SecondaryButton onClick={handleCancelEdit} disabled={sending}>
            Cancel
          </SecondaryButton>
        </>
      )}
      <PrimaryButton
        label="Add Account"
        onClick={() =>
          navigate(`/integrations/netsuite/netsuitecreate`, {
            state: { teamId: props.teamId }
          })
        }
        prefixIcon={React.createElement(IconPlus)}
        className="h-8 text-xs font-medium"
      />
    </div>
  );

  const body = (
    <>
      {/* Account selector (multiple configs) */}
      {props.configs.length > 1 && !isEditing && (
        <div ref={dropdownRef} className="relative mb-4 w-64">
          <button
            className="w-full text-sm font-normal border border-zinc-300 rounded-lg px-4 py-2 text-left focus:ring-1 focus:ring-blue-500 flex items-center justify-between"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span>{selectedConfig?.displayName || "Select account"}</span>
            {isOpen ? (
              <ChevronUp className="w-4 h-4 text-zinc-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-zinc-400" />
            )}
          </button>
          {isOpen && (
            <ul className="absolute w-full mt-1 border border-zinc-300 rounded-lg shadow-lg z-10 p-2 bg-ds-surface">
              {props.configs.map((config) => (
                <li
                  key={config.id}
                  className={`flex justify-between items-center rounded-lg px-3 py-2.5 hover:bg-zinc-100 text-sm cursor-pointer ${config.id === selectedConfigId ? "text-blue-600 bg-zinc-100" : "text-zinc-800"}`}
                  onClick={() => handleSelect(config)}
                >
                  {config.displayName}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Credential card */}
      {selectedConfig && (
        <div className="border border-zinc-200 rounded-xl p-4">
          {/* Config ID + Status row */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-mono text-zinc-400 bg-white border border-zinc-200 px-2 py-0.5 rounded">
              ID: {selectedConfig.id}
            </span>
            {selectedConfig.authType === "oauth2" ||
            (selectedConfig.consumerKey &&
              selectedConfig.consumerSecret &&
              selectedConfig.token &&
              selectedConfig.tokenSecret) ? (
              <HealthBadge />
            ) : (
              <span className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                Incomplete — missing credential fields
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CredentialField
              label="Display Name"
              inputRef={displayNameRef}
              viewValue={selectedConfig.displayName}
              editValue={editFormData.displayName}
              editPlaceholder="e.g. Velaro Production"
              onChange={(v) =>
                setEditFormData({ ...editFormData, displayName: v })
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={displayNameFocused}
            />
            <CredentialField
              label="NetSuite Account ID"
              viewValue={selectedConfig.company}
              editValue={selectedConfig.company}
              disabled
              hint="Read-only — set when the account was created"
            />
            <CredentialField
              label="Consumer Key"
              hint="From NetSuite → Setup → Integration Record"
              inputRef={consumerKeyInputRef}
              viewValue={
                showConsumerKey
                  ? partiallyMaskValue(selectedConfig.consumerKey)
                  : maskValue(selectedConfig.consumerKey)
              }
              editValue={editFormData.consumerKey}
              editPlaceholder="Enter consumer key"
              showReveal
              revealed={showConsumerKey}
              onReveal={() => setShowConsumerKey(!showConsumerKey)}
              onChange={(v) =>
                setEditFormData({ ...editFormData, consumerKey: v })
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={consumerKeyFocused}
            />
            <CredentialField
              label="Consumer Secret"
              hint="From NetSuite → Setup → Integration Record"
              inputRef={consumerSecretInputRef}
              viewValue={
                showConsumerSecret
                  ? partiallyMaskValue(selectedConfig.consumerSecret)
                  : maskValue(selectedConfig.consumerSecret)
              }
              editValue={editFormData.consumerSecret}
              editPlaceholder="Enter consumer secret"
              showReveal
              revealed={showConsumerSecret}
              onReveal={() => setShowConsumerSecret(!showConsumerSecret)}
              onChange={(v) =>
                setEditFormData({ ...editFormData, consumerSecret: v })
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={consumerSecretFocused}
            />
            <CredentialField
              label="Token"
              hint="From NetSuite → Setup → Access Tokens"
              inputRef={tokenInputRef}
              viewValue={
                showToken
                  ? partiallyMaskValue(selectedConfig.token)
                  : maskValue(selectedConfig.token)
              }
              editValue={editFormData.token}
              editPlaceholder="Enter token"
              showReveal
              revealed={showToken}
              onReveal={handleToggleTokenVisibility}
              onChange={(v) => setEditFormData({ ...editFormData, token: v })}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={tokenFocused}
            />
            <CredentialField
              label="Token Secret"
              hint="From NetSuite → Setup → Access Tokens"
              inputRef={tokenSecretInputRef}
              viewValue={
                showTokenSecret
                  ? partiallyMaskValue(selectedConfig.tokenSecret)
                  : maskValue(selectedConfig.tokenSecret)
              }
              editValue={editFormData.tokenSecret}
              editPlaceholder="Enter token secret"
              showReveal
              revealed={showTokenSecret}
              onReveal={handleToggleTokenSecretVisibility}
              onChange={(v) =>
                setEditFormData({ ...editFormData, tokenSecret: v })
              }
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isFocused={tokenSecretFocused}
            />
            <div className="sm:col-span-2">
              <CredentialField
                label="Legacy RESTlet URL (single)"
                hint="Single-script fallback. Use Named RESTlets below to register multiple scripts — the AI will select by name."
                inputRef={restletUrlInputRef}
                viewValue={selectedConfig.restletUrl || ""}
                editValue={editFormData.restletUrl}
                editPlaceholder="https://your-account.restlets.api.netsuite.com/app/site/hosting/restlet.nl?script=...&deploy=1"
                onChange={(v) =>
                  setEditFormData({ ...editFormData, restletUrl: v })
                }
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                isFocused={restletUrlFocused}
              />
            </div>
          </div>
        </div>
      )}

      {/* Transcript auto-push */}
      {selectedConfig && !props.onCreateMode && (
        <SectionCard title="Transcript Auto-Push" className="mb-4">
          <div className="text-sm text-zinc-500 mb-4">
            When enabled, Velaro automatically pushes the full chat transcript
            to the matched NetSuite customer record on every conversation
            resolve. The customer is matched by the conversation contact's email
            address.
          </div>
          <div className="flex items-center justify-between border border-zinc-200 rounded-lg px-4 py-3">
            <div>
              <div className="text-sm font-medium text-zinc-700">
                {selectedConfig.displayName}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {selectedConfig.pushTranscripts
                  ? "Transcript pushed to the matched customer record after each conversation"
                  : "Transcript push is off for this connection"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleTranscriptPush(selectedConfig)}
              disabled={togglingTranscripts}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${selectedConfig.pushTranscripts ? "bg-green-500" : "bg-zinc-300"} ${togglingTranscripts ? "opacity-50 cursor-not-allowed" : ""}`}
              aria-label={
                selectedConfig.pushTranscripts
                  ? "Disable transcript push"
                  : "Enable transcript push"
              }
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${selectedConfig.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
              />
            </button>
          </div>
          {selectedConfig.pushTranscripts && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
              Auto-push active — transcripts will appear on the matched NetSuite
              customer record after each conversation.
            </div>
          )}
          {/* Transcript history mode. Until now this picker existed ONLY in NetSuiteCreate.tsx,
              the legacy Token-Based-Auth wizard, which an OAuth 2.0 connection never sees — so
              "Note per conversation" (full history) was unreachable for every OAuth 2.0 site.
              Found on the V20 production connect 2026-08-05. "native_note" exists in the backend
              but stays unexposed here, matching NetSuiteCreate.tsx — it is not verified against a
              live account yet. */}
          {selectedConfig.pushTranscripts && (
            <div className="mt-4 flex flex-col gap-2">
              <label htmlFor="ns-transcript-mode" className="text-xs font-medium text-zinc-600">
                Transcript history mode
              </label>
              <select
                id="ns-transcript-mode"
                value={selectedConfig.transcriptPushMode || "field"}
                disabled={savingMatchSettings}
                onChange={(e) =>
                  saveMatchSettings(selectedConfig, { transcriptPushMode: e.target.value })
                }
                className="px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50 max-w-md"
              >
                <option value="field">
                  Overwrite single field (default) — most recent transcript only, no history
                </option>
                <option value="restlet_note">
                  Note per conversation — keeps full transcript history
                </option>
              </select>
              {selectedConfig.transcriptPushMode === "restlet_note" && (
                <div className="flex flex-col gap-1 max-w-md mt-1">
                  <label
                    htmlFor="ns-transcript-restlet-url"
                    className="text-xs font-medium text-zinc-600"
                  >
                    Note-creation RESTlet URL
                  </label>
                  <p className="text-xs text-zinc-600">
                    &quot;Note per conversation&quot; mode calls a RESTlet to create a new record
                    each time — required for this mode. The field-metadata RESTlet configured on
                    the credentials card is a separate URL and is not used here. If this is left
                    blank, transcript push silently falls back to overwriting the single field.
                  </p>
                  <input
                    // Uncontrolled (save-on-blur) input: key it to the config id so switching
                    // to another NetSuite connection remounts it instead of showing the
                    // previously selected connection's stale URL.
                    key={`ns-transcript-restlet-url-${selectedConfig.id}`}
                    id="ns-transcript-restlet-url"
                    type="text"
                    defaultValue={selectedConfig.transcriptRestletUrl || ""}
                    disabled={savingMatchSettings}
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next === (selectedConfig.transcriptRestletUrl || "")) return;
                      saveMatchSettings(selectedConfig, { transcriptRestletUrl: next });
                    }}
                    placeholder="https://your-account.restlets.api.netsuite.com/app/site/hosting/restlet.nl?script=...&deploy=1"
                    className="px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50"
                  />
                </div>
              )}
            </div>
          )}
        </SectionCard>
      )}

      {/* Contact Creation Target — Lead vs Customer for AI-created contacts */}
      {selectedConfig && !props.onCreateMode && (
        <SectionCard title="Contact Creation" className="mb-4">
          <div className="text-sm text-zinc-500 mb-4">
            When the AI records a new prospect from a conversation (the{" "}
            <code className="bg-zinc-100 px-1 rounded font-mono text-xs">
              netsuite_create_lead
            </code>{" "}
            skill), choose what kind of NetSuite record it becomes. Individual
            workflows can override this account default from the NetSuite AI
            node editor.
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1 max-w-md">
              <label
                htmlFor="ns-contact-target"
                className="text-xs font-medium text-zinc-600"
              >
                New contacts are created as
              </label>
              <select
                id="ns-contact-target"
                value={selectedConfig.newContactTargetRecordType || "Lead"}
                disabled={savingContactTarget}
                onChange={(e) =>
                  saveContactCreation(selectedConfig, {
                    newContactTargetRecordType: e.target.value
                  })
                }
                className="px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50"
              >
                <option value="Lead">
                  Lead (default) — record enters your Leads list for
                  qualification
                </option>
                <option value="Customer">
                  Customer — record is created directly as an active Customer
                </option>
              </select>
            </div>
            {(selectedConfig.newContactTargetRecordType || "Lead") ===
              "Lead" && (
              <div className="flex flex-col gap-1 max-w-md">
                <label
                  htmlFor="ns-lead-status-id"
                  className="text-xs font-medium text-zinc-600"
                >
                  Lead status internal ID (optional)
                </label>
                <p className="text-[10px] text-zinc-600">
                  The Customer Status your new leads get (NetSuite → Setup →
                  Sales → Customer Statuses → internal ID). Leave blank to
                  auto-detect a status named "Lead".
                </p>
                <div className="flex gap-2">
                  <input
                    id="ns-lead-status-id"
                    type="text"
                    value={
                      leadStatusDraft ??
                      selectedConfig.defaultLeadEntityStatusId ??
                      ""
                    }
                    onChange={(e) =>
                      setLeadStatusDraft(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    placeholder="e.g. 6"
                    disabled={savingContactTarget}
                    className="w-32 px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50"
                  />
                  <SecondaryButton
                    disabled={
                      savingContactTarget ||
                      leadStatusDraft === null ||
                      leadStatusDraft ===
                        (selectedConfig.defaultLeadEntityStatusId ?? "")
                    }
                    onClick={async () => {
                      if (leadStatusDraft === null) return;
                      await saveContactCreation(selectedConfig, {
                        defaultLeadEntityStatusId: leadStatusDraft
                      });
                      setLeadStatusDraft(null);
                    }}
                  >
                    Save
                  </SecondaryButton>
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Transcript match strategy / no-match fallback / field cap */}
      {selectedConfig && !props.onCreateMode && (
        <SectionCard title="Transcript Matching & Fallback" className="mb-4">
          <div className="text-sm text-zinc-500 mb-4">
            Controls how Velaro finds the NetSuite Customer record a transcript
            push should attach to, and what happens when no match is found.
            Defaults match today's behavior exactly — change only if you need
            something different.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="ns-match-strategy"
                className="text-xs font-medium text-zinc-600"
              >
                Match contact by
              </label>
              <select
                id="ns-match-strategy"
                value={selectedConfig.matchStrategy || "emailThenPhone"}
                disabled={savingMatchSettings}
                onChange={(e) =>
                  saveMatchSettings(selectedConfig, {
                    matchStrategy: e.target.value
                  })
                }
                className="px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50"
              >
                <option value="emailThenPhone">
                  Email, then phone (default)
                </option>
                <option value="email">Email only</option>
                <option value="phone">Phone only</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="ns-no-match-action"
                className="text-xs font-medium text-zinc-600"
              >
                When no match is found
              </label>
              <select
                id="ns-no-match-action"
                value={selectedConfig.noMatchAction || "skip"}
                disabled={savingMatchSettings}
                onChange={(e) =>
                  saveMatchSettings(selectedConfig, {
                    noMatchAction: e.target.value
                  })
                }
                className="px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50"
              >
                <option value="skip">
                  Skip (default) — never create a new record
                </option>
                <option value="createLead">
                  Create a Lead, then push the transcript to it
                </option>
                <option value="createCustomer">
                  Create a Customer, then push the transcript to it
                </option>
              </select>
            </div>

            <div className="flex flex-col gap-1 sm:col-span-2 max-w-xs">
              <label
                htmlFor="ns-max-transcript-length"
                className="text-xs font-medium text-zinc-600"
              >
                Max transcript field length (characters)
              </label>
              <p className="text-[10px] text-zinc-400">
                Only applies to the "Overwrite a custom field" push mode.
                Velaro's own ceiling is 100,000 characters (NetSuite's practical
                Long Text field limit) — you can set this lower if your custom
                field has a smaller max, but never higher. Leave blank to use
                the full 100,000-character default.
              </p>
              <div className="flex gap-2">
                <input
                  id="ns-max-transcript-length"
                  type="number"
                  min={1}
                  max={100000}
                  value={
                    maxLengthDraft ??
                    (selectedConfig.maxTranscriptFieldLength
                      ? String(selectedConfig.maxTranscriptFieldLength)
                      : "")
                  }
                  onChange={(e) =>
                    setMaxLengthDraft(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="100000"
                  disabled={savingMatchSettings}
                  className="w-32 px-3 py-2 border border-zinc-300 rounded-lg text-sm text-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-zinc-50"
                />
                <SecondaryButton
                  disabled={
                    savingMatchSettings ||
                    maxLengthDraft === null ||
                    maxLengthDraft ===
                      String(selectedConfig.maxTranscriptFieldLength ?? "")
                  }
                  onClick={async () => {
                    if (maxLengthDraft === null) return;
                    await saveMatchSettings(selectedConfig, {
                      maxTranscriptFieldLength: Number(maxLengthDraft || 0)
                    });
                    setMaxLengthDraft(null);
                  }}
                >
                  Save
                </SecondaryButton>
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Named RESTlets section */}
      {selectedConfig && !props.onCreateMode && (
        <SectionCard
          title="Named RESTlets"
          description="Register multiple custom SuiteScript RESTlets. The AI picks the right one by name (restlet_name)."
          action={
            !addingRestlet ? (
              <SecondaryButton onClick={() => setAddingRestlet(true)}>
                Add RESTlet
              </SecondaryButton>
            ) : null
          }
        >
          {restlets.length === 0 && !addingRestlet && (
            <p className="text-xs text-zinc-400 italic">
              No RESTlets registered yet. Click "Add RESTlet" to register one.
            </p>
          )}

          {restlets.length > 0 && (
            <div className="flex flex-col divide-y divide-zinc-100 mb-4">
              {restlets.map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-zinc-700 bg-zinc-100 px-1.5 py-0.5 rounded">
                        {r.name}
                      </span>
                      <span className="text-xs text-zinc-600">
                        {r.displayName}
                      </span>
                    </div>
                    <p
                      className="text-[10px] text-zinc-400 mt-1 truncate"
                      title={r.url}
                    >
                      {r.url}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <SecondaryButton
                      onClick={() => handleTestRestlet(r.id, r.name)}
                      title="Send a test ping to verify the RESTlet is reachable"
                    >
                      Test
                    </SecondaryButton>
                    <button
                      type="button"
                      onClick={() => handleDeleteRestlet(r.id, r.name)}
                      className="p-1 text-red-400 hover:text-red-600 transition"
                      title="Remove"
                    >
                      <IconTrash2 className="w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {addingRestlet && (
            <div className="flex flex-col gap-3 p-4 rounded-lg border border-zinc-200 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-zinc-600">
                    Name (slug)
                  </label>
                  <p className="text-[10px] text-zinc-400">
                    Used by the AI, e.g.{" "}
                    <code className="bg-zinc-100 px-1 rounded">
                      warranty-check
                    </code>
                  </p>
                  <input
                    type="text"
                    value={newRestlet.name}
                    onChange={(e) =>
                      setNewRestlet({
                        ...newRestlet,
                        name: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "-")
                      })
                    }
                    placeholder="warranty-check"
                    className="px-3 py-2 border rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-zinc-600">
                    Display Name
                  </label>
                  <p className="text-[10px] text-zinc-400">
                    Human label shown in admin, e.g.{" "}
                    <code className="bg-white px-1 rounded">
                      Warranty Check
                    </code>
                  </p>
                  <input
                    type="text"
                    value={newRestlet.displayName}
                    onChange={(e) =>
                      setNewRestlet({
                        ...newRestlet,
                        displayName: e.target.value
                      })
                    }
                    placeholder="Warranty Check"
                    className="px-3 py-2 border rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-zinc-600">
                  RESTlet URL
                </label>
                <input
                  type="text"
                  value={newRestlet.url}
                  onChange={(e) =>
                    setNewRestlet({ ...newRestlet, url: e.target.value })
                  }
                  placeholder="https://your-account.restlets.api.netsuite.com/app/site/hosting/restlet.nl?script=...&deploy=1"
                  className="px-3 py-2 border rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <PrimaryButton
                  onClick={handleAddRestlet}
                  disabled={
                    savingRestlet ||
                    !newRestlet.name ||
                    !newRestlet.displayName ||
                    !newRestlet.url
                  }
                >
                  {savingRestlet ? "Saving…" : "Save RESTlet"}
                </PrimaryButton>
                <SecondaryButton
                  onClick={() => {
                    setAddingRestlet(false);
                    setNewRestlet({ name: "", displayName: "", url: "" });
                  }}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {props.configs.length === 0 && !props.onCreateMode && (
        <div className="border border-dashed border-zinc-300 rounded-xl p-8 text-center">
          <p className="text-zinc-500 text-sm mb-3">
            No NetSuite account connected yet.
          </p>
          <PrimaryButton
            label="Connect NetSuite"
            onClick={() =>
              navigate(`/integrations/netsuite/netsuitecreate`, {
                state: { teamId: props.teamId }
              })
            }
            prefixIcon={React.createElement(IconPlus)}
            className="mx-auto"
          />
        </div>
      )}
    </>
  );

  return (
    <>
      {props.onCreateMode ? (
        body
      ) : (
        <SectionCard
          title="Connected Account"
          description="OAuth credentials for NetSuite token-based auth. Stored encrypted."
          action={headerActions}
          className="mb-4"
        >
          {body}
        </SectionCard>
      )}

      {isModalOpen && (
        <Modal show={isModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8  text-[#EE3763]" />
                <div className="text-zinc-800 text-xl font-medium">
                  Remove Account
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)}>
                <IconClose className="w-8 text-zinc-500" />
              </button>
            </div>

            <div className="text-center text-zinc-800 text-sm font-normal">
              Are you sure you want to remove the account{" "}
              {configToDelete?.displayName}? This cannot be undone and will
              remove all mapped fields.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <SecondaryButton onClick={() => setIsModalOpen(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={() => confirmDelete(configToDelete?.id)}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
}
